import { z } from 'zod';
import { PolygonSchema } from './geojson';

/**
 * Sectors (Task #64) — an operator-drawn geographic zone-of-concern on an
 * incident (IMS §5.3 geographic division/sector; operator-cop-doctrine §2.1
 * Field-IC gap, §2.3 Municipal EOC, §4.3 sectoring overlay, §6.6 free-text
 * lead-label decision).
 *
 * The detail view rolls up the anomalies / response resources / cameras /
 * communities geographically INSIDE the sector polygon — a spatial query, the
 * same idiom as R.3 nearest. Free-text `lead_label` for MVP (real role-based
 * assignment gated on auth/RBAC — follow-up filed). Soft-closed (closed_at)
 * rather than hard-deleted so the SITREP + audit trail keep referencing it.
 */

const UUID = z.string().uuid();

/**
 * A GeoJSON Polygon ring. Validation note: PostGIS ST_IsValid (server-side)
 * rejects self-intersecting rings; here we only enforce the structural minimum
 * (a linear ring needs ≥ 4 positions — 3 distinct + the closing repeat).
 */
export const SectorPolygonSchema = PolygonSchema.refine(
  (g) => g.coordinates.length > 0 && g.coordinates[0]!.length >= 4,
  { message: 'polygon outer ring needs at least 4 positions (a closed triangle)' },
);

/* ------------------------------------------------------------------ *
 * Create — POST /api/sectors
 * ------------------------------------------------------------------ */

export const SectorCreateSchema = z.object({
  session_id: z.string().min(1).max(128),
  incident_id: UUID,
  name: z.string().min(1).max(200),
  /** Operator-drawn GeoJSON Polygon (lon,lat rings). */
  geometry: SectorPolygonSchema,
  /** Free-text "Sector Lead: J. Smith" (§6.6). */
  lead_label: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),
});
export type SectorCreate = z.infer<typeof SectorCreateSchema>;

/** PATCH /api/sectors/:id/geometry — operator redraws the polygon. */
export const SectorGeometryPatchSchema = z.object({
  session_id: z.string().min(1).max(128),
  geometry: SectorPolygonSchema,
});
export type SectorGeometryPatch = z.infer<typeof SectorGeometryPatchSchema>;

/** PATCH /api/sectors/:id/metadata — name / lead_label / notes (≥ 1 field). */
export const SectorMetadataPatchSchema = z
  .object({
    session_id: z.string().min(1).max(128),
    name: z.string().min(1).max(200).optional(),
    /** Pass null to clear the lead label; omit to leave unchanged. */
    lead_label: z.string().max(200).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .refine(
    (v) => v.name !== undefined || v.lead_label !== undefined || v.notes !== undefined,
    { message: 'at least one of name / lead_label / notes is required' },
  );
export type SectorMetadataPatch = z.infer<typeof SectorMetadataPatchSchema>;

/** PATCH /api/sectors/:id/close — soft-close (sets closed_at). */
export const SectorCloseSchema = z.object({
  session_id: z.string().min(1).max(128),
});
export type SectorClose = z.infer<typeof SectorCloseSchema>;

/* ------------------------------------------------------------------ *
 * Read shapes
 * ------------------------------------------------------------------ */

/** The full sector, serialized for the wire (geometry as GeoJSON Polygon). */
export const SectorSchema = z.object({
  id: z.string(),
  incident_id: z.string(),
  incident_number: z.string().nullable(),
  name: z.string(),
  geometry: PolygonSchema,
  lead_label: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  created_by_operator_id: z.string().nullable(),
  closed_at: z.string().nullable(),
});
export type Sector = z.infer<typeof SectorSchema>;

export const SectorListResponseSchema = z.object({
  sectors: z.array(SectorSchema),
});
export type SectorListResponse = z.infer<typeof SectorListResponseSchema>;

/* ------------------------------------------------------------------ *
 * Detail rollup — GET /api/sectors/:id
 * The signals geographically inside the sector polygon, grouped for the panel.
 * ------------------------------------------------------------------ */

const SectorRollupItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** A small kicker — source / class / status, rendered muted in the panel. */
  kicker: z.string().nullable(),
});

export const SectorRollupSchema = z.object({
  anomalies: z.object({
    total: z.number().int(),
    /** High-risk fused hotspots whose point falls inside the polygon. */
    high_risk_hotspots: z.array(SectorRollupItemSchema),
    /** Uncorroborated smoke_detected anomalies (by camera anchor) inside. */
    smoke_detections: z.array(SectorRollupItemSchema),
    /** CAP alert polygons that OVERLAP the sector polygon. */
    cap_alerts: z.array(SectorRollupItemSchema),
  }),
  response_assets: z.object({
    total: z.number().int(),
    /** Count by asset_class, e.g. { fire_station: 3, aviation_base: 1 }. */
    by_class: z.record(z.number().int()),
    items: z.array(SectorRollupItemSchema),
  }),
  cameras: z.object({
    total: z.number().int(),
    items: z.array(SectorRollupItemSchema),
  }),
  communities: z.object({
    total: z.number().int(),
    items: z.array(SectorRollupItemSchema),
  }),
});
export type SectorRollup = z.infer<typeof SectorRollupSchema>;

export const SectorDetailSchema = SectorSchema.extend({
  rollup: SectorRollupSchema,
});
export type SectorDetail = z.infer<typeof SectorDetailSchema>;
